import { api } from './client';

export type GameActiveState =
  | 'active' | 'inactive' | 'failed' | 'activating' | 'deactivating' | 'unknown';

export type GameAction = 'start' | 'stop' | 'restart';

export interface GameStatus {
  active: GameActiveState;
  enabled: boolean;
  activeSince?: string;
  pid?: number;
  ram_bytes?: number;
  cpu_percent?: number;
}

export interface GameItem {
  id: string;
  name: string;
  provider: 'enshrouded' | 'satisfactory' | 'icarus' | 'windrose' | 'starrupture';
  thumbnail: string;
  installing: boolean;
  install_error: string | null;
  updating: boolean;
  update_error: string | null;
  update_available: boolean;
  installed_build_id: string | null;
  steam_beta_branch: string | null;
  workshop_mods: number[];
  public_status: boolean;
  update: {
    scheduled: string | null;
    backup_before: boolean;
    restart_after: boolean;
  };
  auto_restart: {
    enabled: boolean;
    schedule: string | null;
  };
  rcon: {
    enabled: boolean;
    port: number | null;
  };
  console_shortcuts: Array<{ label: string; command: string }>;
  ports: Array<{ port: number; protocol: 'tcp' | 'udp'; description?: string }>;
  status: GameStatus;
}

export interface PortProbeResult {
  port: number;
  protocol: 'tcp' | 'udp';
  description?: string;
  open: boolean;
}

export const fetchGames = (): Promise<{ games: GameItem[] }> =>
  api.get<{ games: GameItem[] }>('/games').then((r) => r.data);

export const controlGame = (id: string, action: GameAction): Promise<void> =>
  api.post(`/games/${id}/${action}`).then(() => undefined);

export const fetchPortCheck = (id: string): Promise<{ ports: PortProbeResult[] }> =>
  api.get<{ ports: PortProbeResult[] }>(`/games/${id}/portcheck`).then((r) => r.data);

export interface ProviderInfo {
  id: string;
  displayName: string;
  steamAppId: number;
  needsWine: boolean;
  defaultPorts: Array<{ port: number; protocol: 'tcp' | 'udp'; description: string }>;
  defaultSaveSubpath: string;
}

export const fetchProviders = (): Promise<{ providers: ProviderInfo[] }> =>
  api.get<{ providers: ProviderInfo[] }>('/games/providers').then((r) => r.data);

export interface CreateGamePayload {
  provider_id: string;
  game_id: string;
  name: string;
  thumbnail_url?: string;
  ports?: Array<{ port: number; protocol: 'tcp' | 'udp'; description?: string }>;
  auto_firewall: boolean;
  auto_backup_on_stop: boolean;
  scheduled_backup: string | null;
  steam_beta_branch?: string | null;
  workshop_mods?: number[];
}

export const createGame = (payload: CreateGamePayload): Promise<{ ok: boolean; game_id: string }> =>
  api.post<{ ok: boolean; game_id: string }>('/games', payload).then((r) => r.data);

export interface InstallJobStatus {
  state: 'queued' | 'running' | 'succeeded' | 'failed' | 'aborted';
  started_at: string | null;
  finished_at: string | null;
  exit_code: number | null;
  error: string | null;
}

export const fetchInstallStatus = (id: string): Promise<InstallJobStatus> =>
  api.get<InstallJobStatus>(`/games/${id}/install-status`).then((r) => r.data);

export interface BackupMeta {
  ts: string;
  created_at: string;
  size_bytes: number;
}

export interface BackupEntry {
  path: string;
  size_bytes: number;
  type: 'file' | 'directory' | 'other';
}

export interface BackupPreview {
  ts: string;
  total_files: number;
  entries: BackupEntry[];
  truncated: boolean;
}

export const fetchBackups = (id: string): Promise<{ backups: BackupMeta[] }> =>
  api.get<{ backups: BackupMeta[] }>(`/games/${id}/backups`).then((r) => r.data);

export const createBackup = (id: string): Promise<BackupMeta> =>
  api.post<BackupMeta>(`/games/${id}/backups`).then((r) => r.data);

export const fetchBackupPreview = (id: string, ts: string): Promise<BackupPreview> =>
  api.get<BackupPreview>(`/games/${id}/backups/${ts}/preview`).then((r) => r.data);

export const restoreBackup = (id: string, ts: string, restart_after = false): Promise<{ ok: boolean }> =>
  api.post<{ ok: boolean }>(`/games/${id}/backups/${ts}/restore`, { restart_after }).then((r) => r.data);

export const deleteBackup = (id: string, ts: string): Promise<{ ok: boolean }> =>
  api.delete<{ ok: boolean }>(`/games/${id}/backups/${ts}`).then((r) => r.data);

export interface UploadInspection {
  entries: BackupEntry[];
  entry_count: number;
  total_uncompressed_bytes: number;
  warnings: string[];
  upload_token: string;
}

export const uploadSavegame = (
  id: string,
  file: File,
  onProgress?: (pct: number) => void,
): Promise<UploadInspection> => {
  const form = new FormData();
  form.append('file', file);
  return api.post<UploadInspection>(`/games/${id}/upload`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: (e) => {
      if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
    },
  }).then((r) => r.data);
};

export const confirmUpload = (
  id: string,
  upload_token: string,
  restart_after = false,
): Promise<{ ok: boolean }> =>
  api.post<{ ok: boolean }>(`/games/${id}/upload/confirm`, { upload_token, restart_after }).then((r) => r.data);

export interface AggregateBackup extends BackupMeta {
  game_id: string;
  game_name: string;
}

export interface DiskInfo {
  total: number;
  used: number;
  free: number;
}

export const fetchAllBackups = (): Promise<{ backups: AggregateBackup[]; disk: DiskInfo | null }> =>
  api.get<{ backups: AggregateBackup[]; disk: DiskInfo | null }>('/backups').then((r) => r.data);

export const bulkDeleteBackups = (
  params: { game_id?: string; older_than_days?: number },
): Promise<{ ok: boolean; deleted: number }> =>
  api.delete<{ ok: boolean; deleted: number }>('/backups/bulk', { data: params }).then((r) => r.data);

export interface UpdateGamePayload {
  name?: string;
  thumbnail_url?: string;
  ports?: Array<{ port: number; protocol: 'tcp' | 'udp'; description?: string }>;
  auto_firewall?: boolean;
  auto_backup_on_stop?: boolean;
  scheduled_backup?: string | null;
  public_status?: boolean;
  update_scheduled?: string | null;
  update_backup_before?: boolean;
  update_restart_after?: boolean;
  steam_beta_branch?: string | null;
  workshop_mods?: number[];
  auto_restart_enabled?: boolean;
  auto_restart_schedule?: string | null;
}

export const updateGame = (id: string, payload: UpdateGamePayload): Promise<{ ok: boolean }> =>
  api.put<{ ok: boolean }>(`/games/${id}`, payload).then((r) => r.data);

export const deleteGame = (
  id: string,
  opts: { purge_firewall?: boolean; purge_backups?: boolean; purge_install?: boolean },
): Promise<{ ok: boolean }> =>
  api.delete<{ ok: boolean }>(`/games/${id}`, { params: opts }).then((r) => r.data);

// ── Savegame download / browse (WP41) ─────────────────────────────────────

export interface SavegameBrowseEntry {
  name: string;
  type: 'file' | 'directory';
  size_bytes: number | null;
  modified_at: string;
}

export const fetchSavegameDownloadToken = (id: string): Promise<{ token: string }> =>
  api.post<{ token: string }>(`/games/${id}/savegame/download-token`).then((r) => r.data);

export function triggerSavegameDownload(id: string, token: string): void {
  window.location.href = `/api/games/${encodeURIComponent(id)}/savegame/download?token=${encodeURIComponent(token)}`;
}

export const fetchSavegameBrowse = (id: string): Promise<{ entries: SavegameBrowseEntry[] }> =>
  api.get<{ entries: SavegameBrowseEntry[] }>(`/games/${id}/savegame/browse`).then((r) => r.data);

// ── Admin Steam IDs (WP37) ─────────────────────────────────────────────────

export const fetchGameAdmins = (id: string): Promise<{ admin_steam_ids: string[] }> =>
  api.get<{ admin_steam_ids: string[] }>(`/games/${id}/admins`).then((r) => r.data);

export const addGameAdmin = (id: string, steam_id: string): Promise<{ admin_steam_ids: string[] }> =>
  api.post<{ admin_steam_ids: string[] }>(`/games/${id}/admins`, { steam_id }).then((r) => r.data);

export const removeGameAdmin = (id: string, steamId: string): Promise<void> =>
  api.delete(`/games/${id}/admins/${steamId}`).then(() => undefined);

// ── Game Update (WP35) ─────────────────────────────────────────────────────

export interface UpdateJobStatus {
  state: 'queued' | 'running' | 'succeeded' | 'failed';
  started_at: string | null;
  finished_at: string | null;
  exit_code: number | null;
  error: string | null;
  restart_after: boolean;
}

export const startGameUpdate = (
  id: string,
  payload: { backup_before: boolean; restart_after: boolean },
): Promise<{ ok: boolean; websocket_channel: string }> =>
  api.post<{ ok: boolean; websocket_channel: string }>(`/games/${id}/update`, payload).then((r) => r.data);

export const fetchUpdateStatus = (id: string): Promise<UpdateJobStatus> =>
  api.get<UpdateJobStatus>(`/games/${id}/update/status`).then((r) => r.data);

export const cancelGameUpdate = (id: string): Promise<{ ok: boolean }> =>
  api.post<{ ok: boolean }>(`/games/${id}/update/cancel`).then((r) => r.data);

// ── Steam News / Changelog (WP106) ────────────────────────────────────────

export interface NewsEntry {
  gid: string;
  title: string;
  url: string;
  date: number;
  contents: string;
}

export const fetchGameChangelog = (id: string): Promise<{ news: NewsEntry[] }> =>
  api.get<{ news: NewsEntry[] }>(`/games/${id}/steam-news`).then((r) => r.data);
